<template>
  <!-- Presentational marketing content for the dark brand panel of
       AuthSplitLayout. Always-dark by design, so it uses the dark md-surface
       tokens + white/divider tokens directly (no `dark:` prefixes). Fully
       data-driven via props so every auth page can reuse it with its own
       (i18n-sourced) copy. -->
  <div class="flex flex-col gap-8">
    <div>
      <p
        v-if="eyebrow"
        class="mb-3 text-xs font-medium uppercase tracking-wide text-theme-palette-emerald"
      >
        {{ eyebrow }}
      </p>
      <h1 class="text-3xl font-bold leading-tight text-theme-text xl:text-4xl">
        {{ title }}
      </h1>
      <p v-if="subtitle" class="mt-2 text-lg font-medium text-theme-text-secondary">
        {{ subtitle }}
      </p>
      <p v-if="body" class="mt-4 text-sm font-medium leading-relaxed text-theme-text-secondary">
        {{ body }}
      </p>
    </div>

    <!-- Live KPI — reuses the exact home-screen KpiCard for strict consistency.
         AuthSplitLayout already pins dark theme tokens on the brand panel, so
         this wrapper stays visually inert and lets the KPI card own its surface. -->
    <div v-if="kpi">
      <kpi-card :kpi-value="kpi" sparkline-position="side" />
    </div>

    <!-- Optional feature list -->
    <ul v-if="features.length" class="flex flex-col gap-4">
      <li v-for="(feature, index) in features" :key="index" class="flex gap-3">
        <span
          class="flex h-9 w-9 shrink-0 items-center justify-center rounded-theme-sm bg-theme-palette-emerald/10 text-theme-palette-emerald"
        >
          <icon v-if="feature.icon" :path="feature.icon" size="18" />
        </span>
        <span>
          <span class="block text-sm font-semibold text-theme-text">{{ feature.title }}</span>
          <span class="mt-0.5 block text-sm leading-snug text-theme-text-secondary">{{
            feature.description
          }}</span>
        </span>
      </li>
    </ul>

    <!-- Optional stat tiles -->
    <div v-if="stats.length" class="grid grid-cols-3 gap-3">
      <div
        v-for="(stat, index) in stats"
        :key="index"
        class="rounded-theme-md border border-theme-border bg-theme-surface-raised p-3"
      >
        <p class="text-lg font-bold text-theme-text">{{ stat.value }}</p>
        <p class="mt-0.5 text-xs text-theme-text-secondary">{{ stat.label }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
  import Icon from '@/components/Icon';
import KpiCard from '@/components/ui/data-display/KpiCard.vue';

  defineProps({
    eyebrow: { type: String, default: '' },
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    body: { type: String, default: '' },
    /** [{ icon, title, description }] */
    features: { type: Array, default: () => [] },
    /** [{ value, label }] */
    stats: { type: Array, default: () => [] },
    /** A KpiValueResponse-shaped object passed straight to the home KpiCard. */
    kpi: { type: Object, default: null },
  });
</script>
